// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { SecondaryActionButton } from '@/components/ui/SecondaryActionButton'
import { CheckCircle2 } from 'lucide-react'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { Textarea }        from '@/components/ui'
import { Input }           from '@/components/ui/Input'
import { AddressFields }   from '@/components/ui/AddressFields'
import { InfoBlock }       from '@/components/ui/InfoBlock'
import { FUNERAL_PROVIDERS, findOffice } from '@/lib/constants/offices'
import { RadioGroup }      from '@/components/ui/RadioGroup'
import { Card }            from '@/components/ui/Card'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { AlertBanner }     from '@/components/feedback/AlertBanner'
import { useAuthStore }    from '@/store'
import { useAutosave }     from '@/hooks/useAutosave'
import { loadFuneralWishes, saveFuneralWishes, EMPTY_FUNERAL, type FuneralWishesData } from '@/services/wishes'
import { ROUTES, DOSSIER_UITVAART } from '@/lib/constants/routes'
import { useReturnNav } from '@/lib/returnNav'
import { NextStepBlock } from '@/components/process/NextStepBlock'

// ── Opties ─────────────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'burial',    label: 'Begraving',      description: 'Begraven op een begraafplaats' },
  { value: 'cremation', label: 'Crematie',        description: 'Verbranding, as kan worden bijgezet of uitgestrooid' },
  { value: 'natural',   label: 'Natuurbegraving', description: 'Begraving in een natuurgebied' },
  { value: 'open',      label: 'Geen voorkeur',   description: 'Laat dit over aan mijn naasten' },
]

// N2: "Anders" toegevoegd
const CEREMONY_OPTIONS = [
  { value: 'religious', label: 'Religieuze dienst' },
  { value: 'civil',     label: 'Burgerlijke / niet-religieuze plechtigheid' },
  { value: 'private',   label: 'Alleen voor familie en directe vrienden' },
  { value: 'none',      label: 'Geen officiële ceremonie' },
  { value: 'other',     label: 'Anders, namelijk...' },
]

// N3: muziekbron
const MUSIC_SOURCE_OPTIONS = [
  { value: 'spotify',  label: 'Spotify playlist' },
  { value: 'youtube',  label: 'YouTube playlist' },
  { value: 'apple',    label: 'Apple Music playlist' },
  { value: 'manual',   label: 'Handmatig beschrijven' },
]

// N5: rouwadvertentie locatie
const OBITUARY_WHERE_OPTIONS = [
  { value: 'regional',  label: 'Regionale krant' },
  { value: 'national',  label: 'Landelijke krant' },
  { value: 'other',     label: 'Anders' },
]

// ── Helpers ────────────────────────────────────────────────────────────────────

function musicSourceLabel(src: string) {
  return MUSIC_SOURCE_OPTIONS.find(o => o.value === src)?.label ?? ''
}

function musicPlaceholder(src: string) {
  if (src === 'spotify') return 'Plak hier uw Spotify playlist-link'
  if (src === 'youtube') return 'Plak hier uw YouTube playlist-link'
  if (src === 'apple')   return 'Plak hier uw Apple Music playlist-link'
  return 'Bijv: Bach cello suite nr. 1, Fields of Gold van Sting...'
}

// ── Pagina ─────────────────────────────────────────────────────────────────────

export function FuneralWishesPage() {
  const { userId }  = useAuthStore()
  const navigate    = useNavigate()
  const { target, label } = useReturnNav()
  const [data,      setData]      = useState<FuneralWishesData>(EMPTY_FUNERAL)
  const dataRef = useRef<FuneralWishesData>(EMPTY_FUNERAL)  // altijd actueel, geen stale closure
  const [isLoading,  setIsLoading]  = useState(true)
  const [loadFailed,  setLoadFailed]  = useState(false)  // V41: key ontbrak bij load
  const [status,    setStatus]    = useState<'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'>('NOT_STARTED')

  useEffect(() => {
    if (!userId) return
    loadFuneralWishes(userId).then(loaded => {
      if (loaded) {
        dataRef.current = loaded; setData(loaded); recalc(loaded)
      } else {
        // V41: null → ofwel leeg nieuw record ofwel key weg na reload
        // decodeContent retourneert null bij ontbrekende key - autosave geblokkeerd via loadFailed
        import('@/lib/db').then(({ db }) => {
          db.funeral_wishes.where('userId').equals(userId).first().then(rec => {
            if (rec?.encryptedContent && rec.blobIv) {
              // Record bestaat en is versleuteld maar kon niet worden geladen → key weg
              setLoadFailed(true)
            }
            // Geen record → normaal nieuw/leeg → autosave mag
          })
        })
      }
      setIsLoading(false)
    }).catch(() => {
      setIsLoading(false)
    })
  }, [userId])

  function recalc(d: FuneralWishesData) {
    const fields = [
      d.type,
      d.ceremonyType,
      d.locationWish || d.locationVenue,
      d.music || d.musicUrl,
      d.clothing,
    ]
    const n = fields.filter(Boolean).length
    const p = Math.round((n / 5) * 100)
    setStatus(p === 0 ? 'NOT_STARTED' : p === 100 ? 'COMPLETE' : 'IN_PROGRESS')
  }

  const { status: saveStatus, saveNow, syncData } = useAutosave({
    data, onSave: async (d) => { if (userId && !loadFailed) await saveFuneralWishes(userId, d) },
    enabled: !isLoading && !loadFailed,
  })

  // upd: direkte persistentie bij elke wijziging
  // Basis = dataRef.current (nooit stale closure), direct opslaan via void
  const upd = useCallback(<K extends keyof FuneralWishesData>(k: K, v: FuneralWishesData[K]) => {
    if (loadFailed || !userId) return
    const nieuw = { ...dataRef.current, [k]: v }  // dataRef.current = altijd actueel
    dataRef.current = nieuw                          // synchronous bijwerken
    syncData(nieuw)                                  // useAutosave dataRef ook bijwerken
    recalc(nieuw)
    setData(nieuw)
    void saveFuneralWishes(userId, nieuw)            // directe persistentie, geen debounce-afhankelijkheid
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loadFailed, syncData])


  if (isLoading) return <LoadingState message="Laden..." />

  if (loadFailed) return (
    <div style={{maxWidth:600,margin:'0 auto',padding:'24px 16px'}}>
      <AlertBanner
        variant="warning"
        title="Uitvaartwensen tijdelijk niet beschikbaar"
        message="Uw uitvaartwensen konden niet worden ontgrendeld. Log opnieuw in om door te gaan. Er is bewust niets opgeslagen of gewijzigd."
      />
    </div>
  )

  return (
    <div>
      <button onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}
        className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group">
        <svg className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        Terug naar dashboard
      </button>



      <ModuleShell
        title="Uitvaartwensen"

        description="Leg vast hoe u wilt worden herdacht. Uw naasten kunnen zo uw wensen respecteren."
        status={status}
        actions={<SaveStatusBadge status={saveStatus} />}
      >

        {/* Begraven of cremeren */}
        <AlertBanner variant="warning" message="Uw uitvaartwensen zijn een persoonlijke leidraad voor uw nabestaanden, geen juridisch bindend document." dismissible />
      <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Begraven of cremeren</h2>
          <RadioGroup name="type" options={TYPE_OPTIONS} value={data.type} onChange={v => upd('type', v)} />
        </Card>

        {/* N2 - Soort plechtigheid met "Anders" */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Soort plechtigheid</h2>
          <RadioGroup
            name="ceremony"
            options={CEREMONY_OPTIONS}
            value={data.ceremonyType}
            onChange={v => upd('ceremonyType', v)}
          />
          {/* Vrij tekstveld bij "Anders" */}
          {data.ceremonyType === 'other' && (
            <div className="mt-4 animate-slide-down">
              <Input
                label="Omschrijf uw voorkeur"
                value={data.ceremonyTypeOther}
                onChange={e => upd('ceremonyTypeOther', e.target.value)}
                placeholder="Bijv: Humanistische ceremonie, openlucht bijeenkomst, concert..."
                autoFocus
              />
            </div>
          )}
          {/* Wensen voor de plechtigheid */}
          {data.ceremonyType && data.ceremonyType !== 'none' && (
            <div className="mt-4 animate-slide-down">
              <Textarea
                label="Wensen voor de plechtigheid"
                value={data.ceremonyWishes}
                onChange={e => upd('ceremonyWishes', e.target.value)}
                placeholder="Bijv: Ik wil dat er een moment van stilte is, dat mensen herinneringen delen..."
                maxLength={600}
              />
            </div>
          )}
        </Card>

        {/* Voorkeurlocatie - vrij tekstveld (M2/N1 uitgebreide versie volgt na locatie-analyse) */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Voorkeurlocatie</h2>
          <Input
            label="Begraafplaats of crematorium"
            value={data.locationWish}
            onChange={e => upd('locationWish', e.target.value)}
            placeholder="Bijv: RK Begraafplaats Heiloo, Crematorium Westerveld..."
            hint="Locatielijst op woonplaats volgt in een volgende versie."
          />
        </Card>

        {/* Uitvaartondernemer */}
        <div id="uitvaartverzorger" />
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Uitvaartondernemer</h2>
          <div className="flex flex-col gap-3">
            <div>
              <Input
                label="Naam uitvaartverzorger of -ondernemer"
                list="funeral-provider-list"
                value={data.funeralHome}
                onChange={e => {
                  const name = e.target.value
                  const o = findOffice(FUNERAL_PROVIDERS, name)
                  upd('funeralHome', o ? o.name : name)
                  if (o) {
                    if (o.street)      upd('funeralHomeStreet', o.street)
                    if (o.houseNumber) upd('funeralHomeHouseNumber', o.houseNumber)
                    if (o.postalCode)  upd('funeralHomePostcode', o.postalCode)
                    if (o.city)        upd('funeralHomeCity', o.city)
                    if (o.phone)       upd('funeralHomePhone', o.phone)
                    if (o.email)       upd('funeralHomeEmail', o.email)
                    if (o.website)     upd('funeralHomeWebsite', o.website)
                  }
                }}
                placeholder="Zoek of typ de naam..."
                hint="Vaak herinnert men zich de plaats eerder dan de exacte naam; vul hieronder de plaats in."
              />
              <datalist id="funeral-provider-list">
                {FUNERAL_PROVIDERS.map(o => <option key={o.name} value={o.name} />)}
              </datalist>
            </div>
            <AddressFields
              showAddition={false}
              values={{
                postalCode:  data.funeralHomePostcode ?? '',
                houseNumber: data.funeralHomeHouseNumber ?? '',
                street:      data.funeralHomeStreet ?? '',
                city:        data.funeralHomeCity ?? '',
              }}
              onChange={(field, val) => {
                const map: Partial<Record<string, keyof FuneralWishesData>> = {
                  postalCode: 'funeralHomePostcode', houseNumber: 'funeralHomeHouseNumber', street: 'funeralHomeStreet', city: 'funeralHomeCity',
                }
                const key = map[field]
                if (key) upd(key, val)
              }}
              labels={{ street: 'Straat', houseNumber: 'Huisnummer', city: 'Plaats' }}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Telefoonnummer (optioneel)"
                type="tel"
                value={data.funeralHomePhone ?? ''}
                onChange={e => upd('funeralHomePhone', e.target.value)}
                placeholder="Bijv: 072 123 45 67"
              />
              <Input
                label="E-mailadres (optioneel)"
                type="email"
                value={data.funeralHomeEmail ?? ''}
                onChange={e => upd('funeralHomeEmail', e.target.value)}
                placeholder="Bijv: info@uitvaartonderneming.nl"
              />
            </div>
            <Input
              label="Website (optioneel)"
              type="url"
              value={data.funeralHomeWebsite ?? ''}
              onChange={e => upd('funeralHomeWebsite', e.target.value)}
              placeholder="Bijv: www.uitvaartonderneming.nl"
            />
            <Input
              label="Contactpersoon (optioneel)"
              value={data.funeralHomeContact ?? ''}
              onChange={e => upd('funeralHomeContact', e.target.value)}
              placeholder="Bijv: mevrouw Koster"
              hint="Naam van de persoon die u wenst voor de uitvaartbegeleiding."
            />
            <Textarea
              label="Toelichting (optioneel)"
              value={data.funeralHomeNotes ?? ''}
              onChange={e => upd('funeralHomeNotes', e.target.value)}
              placeholder="Bijv: zij kent onze familie al jaren en weet wat wij belangrijk vinden"
              maxLength={300}
            />
          </div>
          <div className="mt-4">
            <InfoBlock title="Tip van WilsApp" variant="info" defaultOpen={true}>
              <p>Heeft u al eerder contact gehad met een uitvaartverzorger of specifieke uitvaartbegeleider? Noteer dan ook de naam van uw contactpersoon. Dat geeft uw nabestaanden direct een vertrouwd aanspreekpunt.</p>
            </InfoBlock>
          </div>
        </Card>

        {/* N3 - Muziek & Sprekers met playlist-links */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Muziek & Sprekers</h2>
          <div className="flex flex-col gap-4">

            {/* Muziekbron kiezen */}
            <div>
              <p className="text-sm font-semibold text-ink mb-2">Hoe wilt u uw muziekwensen doorgeven?</p>
              <div className="flex flex-col gap-2">
                {MUSIC_SOURCE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => upd('musicSource', opt.value)}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      data.musicSource === opt.value
                        ? 'border-sage-600 bg-sage-50'
                        : 'border-sand-300 hover:border-sage-300'
                    }`}
                  >
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      data.musicSource === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                    }`} />
                    <span className="text-sm text-ink">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* URL-veld bij playlist-keuze */}
            {(data.musicSource === 'spotify' || data.musicSource === 'youtube' || data.musicSource === 'apple') && (
              <div className="animate-slide-down">
                <Input
                  label={`${musicSourceLabel(data.musicSource)} URL`}
                  value={data.musicUrl}
                  onChange={e => upd('musicUrl', e.target.value)}
                  placeholder={musicPlaceholder(data.musicSource)}
                  type="url"
                  hint="Uw naasten kunnen de afspeellijst direct openen via deze link."
                />
              </div>
            )}

            {/* Handmatig tekstveld - altijd bij 'manual', optioneel aanvullend bij playlist */}
            {(data.musicSource === 'manual' || data.musicSource === '') && (
              <Textarea
                label="Muziekwensen"
                value={data.music}
                onChange={e => upd('music', e.target.value)}
                placeholder="Bijv: Bach cello suite nr. 1, Fields of Gold van Sting, In mijn leven van The Beatles..."
                maxLength={400}
              />
            )}
            {(data.musicSource === 'spotify' || data.musicSource === 'youtube' || data.musicSource === 'apple') && (
              <Textarea
                label="Aanvullende muziekwensen (optioneel)"
                value={data.music}
                onChange={e => upd('music', e.target.value)}
                placeholder="Bijv: ook dit lied als er tijd is..."
                maxLength={200}
              />
            )}

            <Textarea
              label="Sprekers / wie wil ik het woord geven?"
              value={data.ceremonyWishes || ''}
              onChange={e => upd('ceremonyWishes', e.target.value)}
              placeholder="Bijv: Mijn dochter Emma, mijn collega Pieter..."
              maxLength={300}
            />
          </div>
        </Card>

        {/* Uiterlijk & Bloemen */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Uiterlijk & Bloemen</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Bloemenwensen"
              value={data.flowers}
              onChange={e => upd('flowers', e.target.value)}
              placeholder="Bijv: Witte rozen, geen plastic"
            />
            <Input
              label="Kledingwensen"
              value={data.clothing}
              onChange={e => upd('clothing', e.target.value)}
              placeholder="Bijv: Mijn blauwe kostuum"
            />
          </div>
        </Card>

        {/* Rouwkaart & Uitnodigingen */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Rouwkaart & Uitnodigingen</h2>
          <div className="flex flex-col gap-4">
            <Textarea
              label="Rouwkaarttekst"
              value={data.obituaryText}
              onChange={e => upd('obituaryText', e.target.value)}
              placeholder="Bijv: Na een leven vol liefde voor zijn/haar familie en muziek..."
              maxLength={500}
              showCount
            />
            <Textarea
              label="Wie uitnodigen"
              value={data.notificationList}
              onChange={e => upd('notificationList', e.target.value)}
              placeholder="Bijv: Werkgever, buren, koor, bridgeclubs..."
              maxLength={400}
            />
            <Textarea
              label="Wie liever niet uitnodigen"
              value={data.specialRequests}
              onChange={e => upd('specialRequests', e.target.value)}
              placeholder="Optioneel - persoonlijke toelichting..."
              maxLength={300}
            />
          </div>
        </Card>

        {/* N5 - Rouwadvertentie */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Rouwadvertentie</h2>
          <p className="text-sm text-ink-3 mb-4">
            Wilt u dat uw naasten een rouwadvertentie plaatsen?
          </p>
          <div className="flex flex-col gap-2">
            {[
              { value: 'yes', label: 'Ja, ik wil een rouwadvertentie' },
              { value: 'no',  label: 'Nee, geen rouwadvertentie' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => upd('obituaryAd', opt.value)}
                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                  data.obituaryAd === opt.value
                    ? 'border-sage-600 bg-sage-50'
                    : 'border-sand-300 hover:border-sage-300'
                }`}
              >
                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                  data.obituaryAd === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                }`} />
                <span className="text-sm text-ink">{opt.label}</span>
              </button>
            ))}
          </div>

          {data.obituaryAd === 'yes' && (
            <div className="flex flex-col gap-4 mt-4 animate-slide-down">
              <div>
                <p className="text-sm font-semibold text-ink mb-2">Waar plaatsen?</p>
                <div className="flex flex-col gap-2">
                  {OBITUARY_WHERE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => upd('obituaryAdWhere', opt.value)}
                      className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                        data.obituaryAdWhere === opt.value
                          ? 'border-sage-600 bg-sage-50'
                          : 'border-sand-300 hover:border-sage-300'
                      }`}
                    >
                      <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                        data.obituaryAdWhere === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                      }`} />
                      <span className="text-sm text-ink">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                label="Tekst voor de rouwadvertentie"
                value={data.obituaryAdText}
                onChange={e => upd('obituaryAdText', e.target.value)}
                placeholder="Schrijf hier de gewenste advertentietekst..."
                maxLength={600}
                showCount
              />
            </div>
          )}
        </Card>

        {/* Rituelen & Persoonlijke notities */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Rituelen & Persoonlijke notities</h2>
          <Textarea
            label="Rituelen, geloofsovertuiging, persoonlijke wensen"
            value={data.specialRequests || ''}
            onChange={e => upd('specialRequests', e.target.value)}
            placeholder="Bijv: Ik wil een kerkdienst, Joodse tradities volgen, geen religieuze elementen..."
            maxLength={600}
            showCount
          />
        </Card>

      
      {/* Sticky save-status zone - zichtbaar wanneer gebruiker onderaan de pagina scrolt */}
      <div className="sticky bottom-0 left-0 right-0 flex items-center justify-end
                      bg-white/90 backdrop-blur-sm border-t border-sand-200
                      px-4 py-2.5 mt-6 -mx-1 rounded-b-2xl">
        <SaveStatusBadge status={saveStatus} />
      </div>
            {/* Uitvaartverzekering koppelkaart (V40-P1) */}
            <div className="mt-4 rounded-2xl bg-sage-50 border border-sage-200 px-5 py-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-moss flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">€</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-sage-900 mb-1">Uitvaartverzekering</p>
                  <p className="text-xs text-sage-700 leading-relaxed">
                    Leg vast of u een uitvaartverzekering heeft, bij welke verzekeraar
                    en wat het polisnummer is.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={async () => { await saveNow(); navigate(ROUTES.WISHES_FUNERAL_COSTS) }}
                className="flex items-center justify-between rounded-xl border border-sage-300 bg-white px-4 py-2.5 text-sm font-medium text-sage-700 hover:bg-sage-50 hover:border-sage-400 transition-colors"
              >
                <span>Uitvaartverzekering &amp; kosten invullen</span>
                <span className="text-sage-500">→</span>
              </button>
            </div>

            <NextStepBlock
              title="Uw uitvaartwensen delen"
              intro={<>Heeft u uw uitvaartwensen ingevuld? Dan kunt u deze delen met uw uitvaartverzorger of bewaren voor een toekomstig gesprek. Hierdoor weten uw naasten en uw uitvaartverzorger beter wat uw wensen zijn.</>}
              actions={[
                { icon: '📄', label: 'Download uitvaartdossier', primary: true, onClick: () => navigate(DOSSIER_UITVAART) },
                { icon: '📧', label: 'Verstuur voorbereiding naar mijn uitvaartverzorger', onClick: () => {
                  const subject = encodeURIComponent('Voorbereiding uitvaartwensen via WilsApp')
                  const body = encodeURIComponent('Beste,\n\nGraag zou ik mijn uitvaartwensen met u bespreken. Ik heb deze als voorbereiding vastgelegd in WilsApp en kan het overzicht meenemen of meesturen.\n\nDit is een voorbereiding op een gesprek - geen opdracht, overeenkomst of reservering.\n\nMet vriendelijke groet')
                  window.location.href = `mailto:?subject=${subject}&body=${body}`
                } },
                { icon: '➕', label: 'Uitvaartverzorger toevoegen of wijzigen', onClick: () => document.getElementById('uitvaartverzorger')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
              ]}
              disclaimer="Dit is geen opdracht aan de uitvaartverzorger, geen overeenkomst en geen reservering - uitsluitend een voorbereiding op een gesprek."
            />

            <div className="flex flex-col gap-2 mt-2">
              <PrimaryActionButton onClick={async () => { await saveNow(); navigate(target) }} icon={<CheckCircle2 className="h-5 w-5" />}>
          {label}
        </PrimaryActionButton>
              <SecondaryActionButton onClick={async () => { await saveNow(); navigate(-1) }}>Terug naar vorige scherm</SecondaryActionButton>
            </div>
    </ModuleShell>
    </div>
  )
}
